import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Eye, EyeOff, Pin, FileText, Archive, Image as ImageIcon, FileCode, X } from 'lucide-react';
import { supabase } from '../../supabase/client';
import { decode } from 'base64-arraybuffer';

interface DownloadCategory {
  id: number;
  name: string;
  sort_order: number;
  is_visible: boolean;
  created_at: string;
}

interface DownloadFile {
  id: number;
  title: string;
  category_id: number | null;
  summary: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  download_count: number;
  is_pinned: boolean;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  category?: DownloadCategory;
}

export default function DownloadManager() {
  const [activeTab, setActiveTab] = useState<'categories' | 'files'>('files');
  const [categories, setCategories] = useState<DownloadCategory[]>([]);
  const [files, setFiles] = useState<DownloadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | 'all'>('all');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DownloadCategory | null>(null);
  const [editingFile, setEditingFile] = useState<DownloadFile | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set());

  const [categoryForm, setCategoryForm] = useState({ name: '', sort_order: 0, is_visible: true });
  const [fileForm, setFileForm] = useState({
    title: '',
    category_id: '',
    summary: '',
    file_url: '',
    file_name: '',
    file_size: 0,
    file_type: '',
    is_pinned: false,
    is_visible: true,
    sort_order: 0
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [{ data: cats }, { data: fs }] = await Promise.all([
      supabase.from('download_categories').select('*').order('sort_order'),
      supabase.from('download_files').select('*, category:category_id(*)').order('is_pinned', { ascending: false }).order('sort_order')
    ]);
    if (cats) setCategories(cats);
    if (fs) setFiles(fs);
    setLoading(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    if (type?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (type?.includes('zip') || type?.includes('rar') || type?.includes('7z')) return <Archive className="w-5 h-5 text-amber-500" />;
    if (type?.includes('image')) return <ImageIcon className="w-5 h-5 text-green-500" />;
    if (type?.includes('code') || type?.includes('javascript') || type?.includes('python')) return <FileCode className="w-5 h-5 text-blue-500" />;
    return <FileText className="w-5 h-5 text-gray-500" />;
  };

  const saveCategory = async () => {
    if (!categoryForm.name) return;
    if (editingCategory) {
      await supabase.from('download_categories').update(categoryForm).eq('id', editingCategory.id);
    } else {
      await supabase.from('download_categories').insert(categoryForm);
    }
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryForm({ name: '', sort_order: 0, is_visible: true });
    fetchData();
  };

  const deleteCategory = async (id: number) => {
    await supabase.from('download_categories').delete().eq('id', id);
    fetchData();
  };

  const sanitizeFileName = (name: string) => {
    const ext = name.split('.').pop() || '';
    const base = name.substring(0, name.lastIndexOf('.')) || name;
    const sanitized = base
      .replace(/[^\w\u4e00-\u9fa5]/g, '_')
      .replace(/_+/g, '_')
      .substring(0, 50);
    return `${Date.now()}_${sanitized}.${ext}`;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileName = sanitizeFileName(file.name);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64String = (e.target?.result as string)?.split(',')[1];
          if (!base64String) {
            alert('文件读取失败');
            setUploading(false);
            return;
          }

          const arrayBuffer = decode(base64String);

          const { data, error } = await supabase.storage
            .from('downloads')
            .upload(fileName, arrayBuffer, {
              contentType: file.type || 'application/octet-stream',
              cacheControl: '3600',
              upsert: false
            });

          if (error) {
            
            alert('文件上传失败: ' + error.message);
            setUploading(false);
            return;
          }

          if (data) {
            const { data: urlData } = supabase.storage.from('downloads').getPublicUrl(data.path);
            
            setFileForm(prev => ({
              ...prev,
              file_url: urlData.publicUrl,
              file_name: file.name,
              file_size: file.size,
              file_type: file.type
            }));
          }
        } catch (err: any) {
          
          alert('文件上传出错: ' + (err.message || '未知错误'));
        } finally {
          setUploading(false);
        }
      };
      reader.onerror = () => {
        alert('文件读取失败');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      
      alert('文件上传出错: ' + (err.message || '未知错误'));
      setUploading(false);
    }
  };

  const saveFile = async () => {
    if (!fileForm.title || !fileForm.file_url) {
      alert('请填写标题并上传文件');
      return;
    }

    const payload = {
      title: fileForm.title,
      category_id: fileForm.category_id && fileForm.category_id !== '' ? parseInt(fileForm.category_id) : null,
      summary: fileForm.summary,
      file_url: fileForm.file_url,
      file_name: fileForm.file_name,
      file_size: fileForm.file_size,
      file_type: fileForm.file_type,
      is_pinned: fileForm.is_pinned,
      is_visible: fileForm.is_visible,
      sort_order: fileForm.sort_order || 0
    };

    try {
      if (editingFile) {
        const { error } = await supabase.from('download_files').update(payload).eq('id', editingFile.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('download_files').insert(payload);
        if (error) throw error;
      }
      setShowFileModal(false);
      setEditingFile(null);
      setFileForm({ title: '', category_id: '', summary: '', file_url: '', file_name: '', file_size: 0, file_type: '', is_pinned: false, is_visible: true, sort_order: 0 });
      fetchData();
    } catch (err: any) {
      
      alert('保存失败: ' + (err.message || '未知错误'));
    }
  };

  const deleteFile = async (id: number) => {
    await supabase.from('download_files').delete().eq('id', id);
    fetchData();
  };

  const batchDelete = async () => {
    for (const id of selectedFiles) {
      await supabase.from('download_files').delete().eq('id', id);
    }
    setSelectedFiles(new Set());
    fetchData();
  };

  const batchUpdateVisibility = async (isVisible: boolean) => {
    for (const id of selectedFiles) {
      await supabase.from('download_files').update({ is_visible: isVisible }).eq('id', id);
    }
    setSelectedFiles(new Set());
    fetchData();
  };

  const togglePin = async (file: DownloadFile) => {
    await supabase.from('download_files').update({ is_pinned: !file.is_pinned }).eq('id', file.id);
    fetchData();
  };

  const toggleVisibility = async (file: DownloadFile) => {
    await supabase.from('download_files').update({ is_visible: !file.is_visible }).eq('id', file.id);
    fetchData();
  };

  const filteredFiles = files.filter(f => {
    const matchesSearch = f.title.toLowerCase().includes(searchQuery.toLowerCase()) || f.summary?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || f.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">下载中心管理</h2>
        <div className="flex gap-2">
          <button onClick={() => setActiveTab('categories')} className={`px-4 py-2 rounded-lg ${activeTab === 'categories' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>分类管理</button>
          <button onClick={() => setActiveTab('files')} className={`px-4 py-2 rounded-lg ${activeTab === 'files' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>资料管理</button>
        </div>
      </div>

      {activeTab === 'categories' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => { setEditingCategory(null); setCategoryForm({ name: '', sort_order: 0, is_visible: true }); setShowCategoryModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg">
              <Plus className="w-4 h-4" /> 新增分类
            </button>
          </div>
          <div className="bg-white rounded-xl border">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr><th className="px-4 py-3 text-left">分类名称</th><th className="px-4 py-3 text-left">排序</th><th className="px-4 py-3 text-left">状态</th><th className="px-4 py-3 text-left">操作</th></tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.id} className="border-t">
                    <td className="px-4 py-3">{cat.name}</td>
                    <td className="px-4 py-3">{cat.sort_order}</td>
                    <td className="px-4 py-3">{cat.is_visible ? <span className="text-green-600">显示</span> : <span className="text-gray-400">隐藏</span>}</td>
                    <td className="px-4 py-3 flex gap-2">
                      <button onClick={() => { setEditingCategory(cat); setCategoryForm({ name: cat.name, sort_order: cat.sort_order, is_visible: cat.is_visible }); setShowCategoryModal(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => deleteCategory(cat.id)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'files' && (
        <div>
          <div className="flex gap-4 mb-4">
            <div className="flex-1 flex gap-2">
              <input type="text" placeholder="搜索资料..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="flex-1 px-4 py-2 border rounded-lg" />
              <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value === 'all' ? 'all' : parseInt(e.target.value))} className="px-4 py-2 border rounded-lg">
                <option value="all">全部分类</option>
                {categories.filter(c => c.is_visible).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <button onClick={() => { setEditingFile(null); setFileForm({ title: '', category_id: '', summary: '', file_url: '', file_name: '', file_size: 0, file_type: '', is_pinned: false, is_visible: true, sort_order: 0 }); setShowFileModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg"><Plus className="w-4 h-4" /> 新增资料</button>
          </div>

          {selectedFiles.size > 0 && (
            <div className="flex gap-2 mb-4 p-3 bg-blue-50 rounded-lg">
              <span className="text-blue-700">已选择 {selectedFiles.size} 项</span>
              <button onClick={() => batchUpdateVisibility(true)} className="px-3 py-1 bg-green-600 text-white rounded text-sm">批量上架</button>
              <button onClick={() => batchUpdateVisibility(false)} className="px-3 py-1 bg-amber-600 text-white rounded text-sm">批量下架</button>
              <button onClick={batchDelete} className="px-3 py-1 bg-red-600 text-white rounded text-sm">批量删除</button>
            </div>
          )}

          <div className="bg-white rounded-xl border">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3"><input type="checkbox" checked={selectedFiles.size === filteredFiles.length} onChange={e => setSelectedFiles(e.target.checked ? new Set(filteredFiles.map(f => f.id)) : new Set())} /></th>
                  <th className="px-4 py-3 text-left">资料</th>
                  <th className="px-4 py-3 text-left">分类</th>
                  <th className="px-4 py-3 text-left">大小</th>
                  <th className="px-4 py-3 text-left">下载</th>
                  <th className="px-4 py-3 text-left">状态</th>
                  <th className="px-4 py-3 text-left">操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map(file => (
                  <tr key={file.id} className={`border-t ${file.is_pinned ? 'bg-amber-50' : ''}`}>
                    <td className="px-4 py-3"><input type="checkbox" checked={selectedFiles.has(file.id)} onChange={e => { const s = new Set(selectedFiles); e.target.checked ? s.add(file.id) : s.delete(file.id); setSelectedFiles(s); }} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getFileIcon(file.file_type)}
                        <div>
                          <div className="font-medium">{file.title}</div>
                          <div className="text-xs text-gray-500">{file.file_name}</div>
                        </div>
                        {file.is_pinned && <Pin className="w-4 h-4 text-amber-500" />}
                      </div>
                    </td>
                    <td className="px-4 py-3">{file.category?.name || '-'}</td>
                    <td className="px-4 py-3">{formatFileSize(file.file_size)}</td>
                    <td className="px-4 py-3">{file.download_count}</td>
                    <td className="px-4 py-3">{file.is_visible ? <span className="text-green-600">显示</span> : <span className="text-gray-400">隐藏</span>}</td>
                    <td className="px-4 py-3 flex gap-1">
                      <button onClick={() => togglePin(file)} className={`p-2 rounded ${file.is_pinned ? 'text-amber-600 bg-amber-50' : 'text-gray-400'}`}><Pin className="w-4 h-4" /></button>
                      <button onClick={() => toggleVisibility(file)} className={`p-2 rounded ${file.is_visible ? 'text-green-600' : 'text-gray-400'}`}>{file.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                      <button onClick={() => { setEditingFile(file); setFileForm({ title: file.title, category_id: file.category_id?.toString() || '', summary: file.summary || '', file_url: file.file_url, file_name: file.file_name, file_size: file.file_size, file_type: file.file_type, is_pinned: file.is_pinned, is_visible: file.is_visible, sort_order: file.sort_order }); setShowFileModal(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => deleteFile(file.id)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showCategoryModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-2xl p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">{editingCategory ? '编辑分类' : '新增分类'}</h3>
                <button onClick={() => setShowCategoryModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium mb-1">分类名称</label><input type="text" value={categoryForm.name} onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">排序</label><input type="number" value={categoryForm.sort_order} onChange={e => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div className="flex items-center gap-2"><input type="checkbox" checked={categoryForm.is_visible} onChange={e => setCategoryForm({ ...categoryForm, is_visible: e.target.checked })} /><span>显示</span></div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowCategoryModal(false)} className="flex-1 py-2 border rounded-lg">取消</button>
                <button onClick={saveCategory} className="flex-1 py-2 bg-blue-600 text-white rounded-lg">保存</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFileModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">{editingFile ? '编辑资料' : '新增资料'}</h3>
                <button onClick={() => setShowFileModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium mb-1">资料标题</label><input type="text" value={fileForm.title} onChange={e => setFileForm({ ...fileForm, title: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-1">所属分类</label><select value={fileForm.category_id} onChange={e => setFileForm({ ...fileForm, category_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg"><option value="">请选择</option>{categories.filter(c => c.is_visible).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                <div><label className="block text-sm font-medium mb-1">简介</label><textarea value={fileForm.summary} onChange={e => setFileForm({ ...fileForm, summary: e.target.value })} className="w-full px-3 py-2 border rounded-lg h-20" /></div>
                <div><label className="block text-sm font-medium mb-1">文件上传</label><input type="file" onChange={handleFileUpload} className="w-full" disabled={uploading} />{uploading && <div className="mt-2 text-sm text-blue-600">上传中...</div>}{fileForm.file_name && !uploading && <div className="mt-2 text-sm text-green-600">已上传: {fileForm.file_name} ({formatFileSize(fileForm.file_size)})</div>}</div>
                <div className="flex gap-4"><div className="flex items-center gap-2"><input type="checkbox" checked={fileForm.is_pinned} onChange={e => setFileForm({ ...fileForm, is_pinned: e.target.checked })} /><span>置顶</span></div><div className="flex items-center gap-2"><input type="checkbox" checked={fileForm.is_visible} onChange={e => setFileForm({ ...fileForm, is_visible: e.target.checked })} /><span>显示</span></div></div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowFileModal(false)} className="flex-1 py-2 border rounded-lg">取消</button>
                <button onClick={saveFile} className="flex-1 py-2 bg-blue-600 text-white rounded-lg">保存</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
